var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DeleteModelMessage, DisablePeerGizmoMessage, EnterRoomMessage, ExitCurrentRoomMessage, LayoutCreationFinishedMessage, ModuleId, RemoveSubModelMessage, UpdateGizmoMessage, UpdateGizmoModeMessage, UpdateGizmoTransformMessage, GizmoLockFacet } from "flavr-common";
import { Quaternion, Vector3 } from "flavr-math";
import { assertIsWeb } from "@messages/environment/EnvironmentDetection";
import { WebXRPresentingMessage } from "@messages/xr/WebXRMessages";
import { FacetHandler, FacetRemoveHandler, NewModule, MessageHandler } from "@modules/module";
import { GizmoProperties, gizmoService } from "./GizmoService";
assertIsWeb("GizmoModule");
/**
 * Coordinates the shared transform gizmo so selection and editing features can present one consistent manipulation tool.
 *
 * Facets:
 * - `GizmoLockFacet` - Marks models whose gizmo interaction state should react to locking changes.
 *
 * Messages:
 * - `UpdateGizmoMessage`, `UpdateGizmoTransformMessage` and `UpdateGizmoModeMessage` - Control the visible gizmo state and transform mode.
 * - `DisablePeerGizmoMessage` - Lets peer state temporarily suppress gizmo interaction.
 * - `EnterRoomMessage`, `ExitCurrentRoomMessage`, `LayoutCreationFinishedMessage` and `WebXRPresentingMessage` - Reset gizmo visibility when the interaction context changes.
 * - `RemoveSubModelMessage` and `DeleteModelMessage` - Clean up gizmo state for removed models.
 *
 * The actual logic happens in the GizmoService:
 * @see GizmoService.
 */
export class GizmoModule extends NewModule {
    constructor() {
        super(ModuleId.GIZMO);
    }
    /// Gizmo message handlers ///
    handleUpdateGizmoMessage(msg) {
        const position = msg.position == null ? new Vector3() : new Vector3(...msg.position);
        const rotation = msg.rotation == null ? new Quaternion() : new Quaternion(...msg.rotation);
        const scale = msg.scale == null ? new Vector3() : new Vector3(...msg.scale);
        gizmoService.updateGizmo(new GizmoProperties(msg.enable, msg.targetUid, position, rotation, scale, "translate", msg.scalable));
    }
    handleUpdateGizmoTransformMessage(msg) {
        var _a;
        const position = msg.position == null ? new Vector3() : new Vector3(msg.position[0], msg.position[1], msg.position[2]);
        const rotation = msg.rotation == null ? new Quaternion() : new Quaternion(msg.rotation[0], msg.rotation[1], msg.rotation[2], msg.rotation[3]);
        const scale = msg.scale == null ? new Vector3(1, 1, 1) : new Vector3(msg.scale[0], msg.scale[1], msg.scale[2]);
        void gizmoService.setGizmoTransform((_a = msg.targetUid) !== null && _a !== void 0 ? _a : -1, position, rotation, scale, true);
    }
    handleUpdateGizmoType(msg) {
        if (msg.isLocal !== undefined) {
            gizmoService.setGizmoCoordinateSpace(msg.isLocal);
        }
        if (msg.transformMode === -1 || msg.transformMode === undefined)
            return;
        // reset mode
        if (msg.transformMode === 3) {
            gizmoService.resetScale();
            return;
        }
        gizmoService.setGizmoType(msg.transformMode);
    }
    /// Peer message handlers ///
    handleDisablePeerGizmo(msg) {
        if (msg.modelUid === undefined)
            return;
        gizmoService.peerDisablesGizmo(msg.modelUid);
    }
    /// Room message handlers ///
    handleChangingWorldOrRoom() {
        const props = new GizmoProperties(false);
        gizmoService.updateGizmo(props);
    }
    handleGizmoLockFacet(uid) {
        gizmoService.handleGizmoLockChanged(uid);
    }
    handleRemoveGizmoLockFacet(uid) {
        gizmoService.handleGizmoLockChanged(uid);
    }
    /// Model message handlers ///
    handleModelDeleted(msg) {
        gizmoService.handleModelDeleted(msg.uid);
    }
}
__decorate([
    MessageHandler(UpdateGizmoMessage)
], GizmoModule.prototype, "handleUpdateGizmoMessage", null);
__decorate([
    MessageHandler(UpdateGizmoTransformMessage)
], GizmoModule.prototype, "handleUpdateGizmoTransformMessage", null);
__decorate([
    MessageHandler(UpdateGizmoModeMessage)
], GizmoModule.prototype, "handleUpdateGizmoType", null);
__decorate([
    MessageHandler(DisablePeerGizmoMessage)
], GizmoModule.prototype, "handleDisablePeerGizmo", null);
__decorate([
    MessageHandler(EnterRoomMessage, ExitCurrentRoomMessage, LayoutCreationFinishedMessage, WebXRPresentingMessage)
], GizmoModule.prototype, "handleChangingWorldOrRoom", null);
__decorate([
    FacetHandler(GizmoLockFacet)
], GizmoModule.prototype, "handleGizmoLockFacet", null);
__decorate([
    FacetRemoveHandler(GizmoLockFacet)
], GizmoModule.prototype, "handleRemoveGizmoLockFacet", null);
__decorate([
    MessageHandler(RemoveSubModelMessage, DeleteModelMessage)
], GizmoModule.prototype, "handleModelDeleted", null);
